import React, { useState } from 'react';
import { BackButton } from '../ui';
import Die from '../Die';
import DiceBackground from '../DiceBackground';
import { useTranslation } from '../../i18n';

interface HowToPlayProps {
  onBack: () => void;
}

type Tab = 'rules' | 'about';

export const HowToPlay: React.FC<HowToPlayProps> = ({ onBack }) => {
  const t = useTranslation();
  const [activeTab, setActiveTab] = useState<Tab>('rules');

  return (
    <div className="h-[100dvh] flex flex-col items-center justify-start text-white p-4 pt-8 relative select-none bg-[#1a101f] overflow-hidden">
      <DiceBackground count={10} />

      <BackButton onClick={onBack} />

      <h1 className="text-2xl lg:text-5xl font-bold tracking-tighter mb-4 text-theme drop-shadow-lg uppercase text-center relative z-10 mt-4">
        {activeTab === 'rules' ? t.howToPlay : t.aboutTheGame}
      </h1>

      {/* Tab switcher */}
      <div className="flex gap-2 mb-4 relative z-10">
        <button
          onClick={() => setActiveTab('rules')}
          className={`px-3 py-1.5 lg:px-6 lg:py-3 text-[8px] lg:text-xs font-bold uppercase tracking-wider pixel-border transition-all ${activeTab === 'rules'
            ? 'bg-theme text-black'
            : 'bg-black/40 text-gray-500 hover:text-white'
            }`}
        >
          {t.howToPlay}
        </button>
        <button
          onClick={() => setActiveTab('about')}
          className={`px-3 py-1.5 lg:px-6 lg:py-3 text-[8px] lg:text-xs font-bold uppercase tracking-wider pixel-border transition-all ${activeTab === 'about'
            ? 'bg-theme text-black'
            : 'bg-black/40 text-gray-500 hover:text-white'
            }`}
        >
          {t.aboutTheGame}
        </button>
      </div>

      <div className="w-full max-w-2xl flex-1 overflow-y-auto pr-2 custom-scrollbar flex flex-col gap-6 relative z-10">
        {activeTab === 'rules' ? <RulesContent /> : <AboutContent />}
      </div>
    </div>
  );
};

// ─── Rules (original How To Play content) ────────────────────────────────────

const RulesContent: React.FC = () => {
  const t = useTranslation();
  return (
    <>
      <section className="pixel-card p-4 border-l-4 border-l-blue-400 bg-blue-900/20">
        <h2 className="text-blue-400 text-xs lg:text-lg mb-2 uppercase font-bold">{t.htpGoalTitle}</h2>
        <p className="text-[8px] lg:text-xs leading-relaxed text-gray-300">{t.htpGoalText}</p>
      </section>

      <section className="pixel-card p-4 border-l-4 border-l-green-400 bg-green-900/20">
        <h2 className="text-green-400 text-xs lg:text-lg mb-2 uppercase font-bold">{t.htpScoringTitle}</h2>
        <p className="text-[8px] lg:text-xs leading-relaxed text-gray-300">{t.htpScoringText}</p>
        <div className="mt-2 flex gap-4 text-[8px] lg:text-[10px] font-mono">
          <span className="text-yellow-500">2x Match: (Value + Value) * 2</span>
          <span className="text-yellow-500">3x Match: (Value + Value + Value) * 3</span>
        </div>
      </section>

      <section className="pixel-card p-4 border-l-4 border-l-red-400 bg-red-900/20">
        <h2 className="text-red-400 text-xs lg:text-lg mb-2 uppercase font-bold">{t.htpCombatTitle}</h2>
        <p className="text-[8px] lg:text-xs leading-relaxed text-gray-300">
          {t.htpCombatText1} <span className="text-white font-bold">{t.htpCombatRemove}</span> {t.htpCombatText2}
        </p>
      </section>

      <section className="pixel-card p-4 border-l-4 border-l-purple-400 bg-purple-900/20">
        <h2 className="text-purple-400 text-xs lg:text-lg mb-2 uppercase font-bold">{t.htpProgressionTitle}</h2>
        <p className="text-[8px] lg:text-xs leading-relaxed text-gray-300">{t.htpProgressionText}</p>
      </section>

      <section className="pixel-card p-4 border-l-4 border-l-yellow-400 bg-yellow-900/20">
        <h2 className="text-yellow-400 text-xs lg:text-lg mb-2 uppercase font-bold">{t.htpSuperDiceTitle}</h2>
        <p className="text-[8px] lg:text-xs leading-relaxed text-gray-300 mb-3">{t.htpSuperDiceText}</p>
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 shrink-0"><Die value="joker" size="sm" /></div>
            <p className="text-[8px] lg:text-xs text-gray-300">{t.htpJoker}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 shrink-0"><Die value="shield" size="sm" /></div>
            <p className="text-[8px] lg:text-xs text-gray-300">{t.htpShield}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 shrink-0"><Die value="bomb" size="sm" /></div>
            <p className="text-[8px] lg:text-xs text-gray-300">{t.htpBomb}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 shrink-0"><Die value="reroll" size="sm" /></div>
            <p className="text-[8px] lg:text-xs text-gray-300">{t.htpReroll}</p>
          </div>
        </div>
      </section>
    </>
  );
};

// ─── About the Game ──────────────────────────────────────────────────────────

const AboutContent: React.FC = () => {
  const t = useTranslation();
  return (
    <>
      <section className="pixel-card p-4 border-l-4 border-l-theme bg-purple-900/20">
        <h2 className="text-theme text-xs lg:text-lg mb-2 uppercase font-bold">{t.htpWhatIs}</h2>
        <p className="text-[8px] lg:text-xs leading-relaxed text-gray-300">
          {t.htpWhatIsText}{' '}
          <a
            href="https://en.wikipedia.org/wiki/Knucklebones"
            target="_blank"
            rel="noopener noreferrer"
            className="text-theme underline hover:text-purple-300"
          >
            Knucklebones
          </a>
          {t.htpWhatIsText2}
        </p>
      </section>

      <section className="pixel-card p-4 border-l-4 border-l-yellow-400 bg-yellow-900/20">
        <h2 className="text-yellow-400 text-xs lg:text-lg mb-2 uppercase font-bold">{t.htpOriginsTitle}</h2>
        <p className="text-[8px] lg:text-xs leading-relaxed text-gray-300">{t.htpOriginsText1}</p>
        <p className="text-[8px] lg:text-xs leading-relaxed text-gray-300 mt-2">{t.htpOriginsText2}</p>
      </section>

      <section className="pixel-card p-4 border-l-4 border-l-red-400 bg-red-900/20">
        <h2 className="text-red-400 text-xs lg:text-lg mb-2 uppercase font-bold">{t.htpCotlTitle}</h2>
        <p className="text-[8px] lg:text-xs leading-relaxed text-gray-300">
          {t.htpCotlText1}{' '}
          <span className="text-white font-bold">{t.htpCotlName}</span>
          {t.htpCotlText2}
        </p>
        <p className="text-[8px] lg:text-xs leading-relaxed text-gray-300 mt-2">{t.htpCotlText3}</p>
      </section>

      <section className="pixel-card p-4 border-l-4 border-l-blue-400 bg-blue-900/20">
        <h2 className="text-blue-400 text-xs lg:text-lg mb-2 uppercase font-bold">{t.htpCulturesTitle}</h2>
        <p className="text-[8px] lg:text-xs leading-relaxed text-gray-300">{t.htpCulturesIntro}</p>
        <div className="mt-2 flex flex-col gap-1 text-[8px] lg:text-[10px] text-gray-400">
          <p><span className="text-blue-300 font-bold">Mongolia:</span> Shagai — played with sheep ankle bones, inscribed in UNESCO's Intangible Cultural Heritage</p>
          <p><span className="text-blue-300 font-bold">Korea:</span> Gonggi — five pebbles, five escalating levels of difficulty</p>
          <p><span className="text-blue-300 font-bold">Japan:</span> Otedama — small bags of azuki beans, originating from China during the Nara Period</p>
          <p><span className="text-blue-300 font-bold">Philippines:</span> Kuru / Siklot — played with stones, shells, or seeds</p>
          <p><span className="text-blue-300 font-bold">Malaysia:</span> Batu Seremban — "five stones," using seeds or sand-filled bags</p>
          <p><span className="text-blue-300 font-bold">Ancient Rome:</span> Tali — depicted in Pompeii murals, played by goddesses in mythology</p>
        </div>
      </section>

      <section className="pixel-card p-4 border-l-4 border-l-green-400 bg-green-900/20">
        <h2 className="text-green-400 text-xs lg:text-lg mb-2 uppercase font-bold">{t.htpBrowsersTitle}</h2>
        <p className="text-[8px] lg:text-xs leading-relaxed text-gray-300">{t.htpBrowsersText1}</p>
        <p className="text-[8px] lg:text-xs leading-relaxed text-gray-300 mt-2">{t.htpBrowsersText2}</p>
      </section>
    </>
  );
};
